/**
 * Atlas — sound palette.
 *
 * The world is a quiet travel study. The sounds are: a soft brass COMPASS TICK
 * (selection / look-around), a felt PIN DROP onto paper-over-cork (placing the
 * guess), a low woody PAGE TURN (nav / next), a warm ARRIVAL chord that
 * "lands" like stepping off a train somewhere lovely (the reveal), and a gentle
 * SETTLE for the place card turning up. A soft, never-punitive FAR note for a
 * far-afield guess — framed as curiosity, not a buzzer.
 *
 * Entirely bespoke — different frequencies, envelopes, and metaphors from
 * Five's letterpress palette. Every public `play` routes through
 * `audio.playIfNotMuted` so the global mute gate is honored automatically.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type AtlasSoundName =
  | 'tick' // compass / selection
  | 'pinDrop' // dropping the guess pin
  | 'confirm' // confirming the placed guess
  | 'pageTurn' // nav / next round / page
  | 'arriveNear' // warm arrival — a good guess
  | 'arriveFar' // gentle, curious arrival — a far guess (NOT a loss)
  | 'settle' // place card turns up
  | 'star'; // a star stamping at the reveal

export const soundHaptic: Partial<Record<AtlasSoundName, HapticStyle>> = {
  tick: 'light',
  pinDrop: 'medium',
  confirm: 'medium',
  pageTurn: 'light',
  arriveNear: 'success',
  arriveFar: 'light',
  settle: 'light',
  star: 'light',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A short brass tick — a quick band-passed transient with a tiny pitched ring. */
function synthTick(pitch = 1180, gain = 0.12): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;

  const buf = audio.noiseBuffer(ctx, { lengthMs: 14 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = pitch;
  bp.Q.value = 6;
  const { node: ng } = audio.envelope(ctx, { attack: 0.001, release: 0.04, peak: gain });
  audio.connectThrough(src, bp, ng, master);
  src.start(t);
  src.stop(t + 0.05);

  // A whisper of pitched ring so it reads "brass," not "click."
  const osc = audio.makeOscillator(ctx, { freq: pitch * 0.5, type: 'sine' });
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain * 0.5, t + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.14);
}

/** A soft, felt tone with a low-passed body. */
function synthTone(opts: {
  freq: number;
  duration: number;
  peak?: number;
  type?: 'sine' | 'triangle';
  cutoff?: number;
  fifth?: boolean;
}): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const peak = opts.peak ?? 0.16;
  const dur = opts.duration;

  const osc = audio.makeOscillator(ctx, { freq: opts.freq, type: opts.type ?? 'sine' });
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = opts.cutoff ?? 1700;
  lp.Q.value = 0.5;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.012,
    decay: 0.1,
    sustain: 0.55,
    release: dur,
    peak,
  });
  audio.connectThrough(osc, lp, g, master);
  osc.start(t);
  osc.stop(t + dur + 0.06);

  if (opts.fifth) {
    const osc2 = audio.makeOscillator(ctx, { freq: opts.freq * 1.5, type: 'sine' });
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.0001, t);
    g2.gain.exponentialRampToValueAtTime(peak * 0.32, t + 0.03);
    g2.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    audio.connectThrough(osc2, g2, master);
    osc2.start(t);
    osc2.stop(t + dur + 0.06);
  }
}

/** A warm chord arpeggiated very gently — the "arrival." */
function synthChord(freqs: number[], stepMs: number, peak = 0.16): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  freqs.forEach((f, i) => {
    const t = t0 + (i * stepMs) / 1000;
    const osc = audio.makeOscillator(ctx, { freq: f, type: 'triangle' });
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
    audio.connectThrough(osc, lp, g, master);
    osc.start(t);
    osc.stop(t + 1.05);
  });
  // A soft sub for warmth under the chord.
  const sub = audio.makeOscillator(ctx, { freq: freqs[0]! / 2, type: 'sine' });
  const sg = ctx.createGain();
  sg.gain.setValueAtTime(0.0001, t0);
  sg.gain.exponentialRampToValueAtTime(peak * 0.5, t0 + 0.04);
  sg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9);
  audio.connectThrough(sub, sg, master);
  sub.start(t0);
  sub.stop(t0 + 0.95);
}

// ---------------------------------------------------------------------------
// Concrete sounds
// ---------------------------------------------------------------------------

function synthPinDrop(): void {
  // A felt thud onto paper-over-cork: a quick low pitched drop + a soft tap.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 320, type: 'triangle' });
  osc.frequency.exponentialRampToValueAtTime(120, t + 0.12);
  const { node: g } = audio.envelope(ctx, { attack: 0.002, decay: 0.04, sustain: 0.3, release: 0.16, peak: 0.2 });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.22);
  // soft cork tap
  const buf = audio.noiseBuffer(ctx, { lengthMs: 18, shaping: 1.6 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 900;
  const { node: ng } = audio.envelope(ctx, { attack: 0.001, release: 0.05, peak: 0.12 });
  audio.connectThrough(src, lp, ng, master);
  src.start(t);
  src.stop(t + 0.06);
}

function synthConfirm(): void {
  // A small two-note rise — "set." C5 → G5, soft.
  synthTone({ freq: 523.25, duration: 0.18, peak: 0.13, type: 'triangle', cutoff: 2000 });
  window.setTimeout(() => {
    synthTone({ freq: 659.26, duration: 0.26, peak: 0.13, type: 'triangle', cutoff: 2200, fifth: true });
  }, 90);
}

function synthPageTurn(): void {
  // A low woody whoosh — a page of the atlas turning.
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const dur = 0.34;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 380, shaping: 1.5 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(420, t);
  bp.frequency.exponentialRampToValueAtTime(1300, t + dur);
  bp.Q.value = 0.8;
  const { node: g } = audio.envelope(ctx, { attack: 0.02, decay: 0.08, sustain: 0.5, release: dur, peak: 0.14 });
  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + dur + 0.05);
}

function synthArriveNear(): void {
  // A warm major arrival — Csus2-ish: C5, D5, G5, then an octave glow.
  synthChord([523.25, 587.33, 783.99], 120, 0.16);
  window.setTimeout(() => {
    synthTone({ freq: 1046.5, duration: 0.9, peak: 0.1, type: 'sine', cutoff: 3000 });
  }, 360);
}

function synthArriveFar(): void {
  // A gentle, curious open fifth — not minor, not a buzzer. "Oh, look where!"
  synthChord([392.0, 587.33], 140, 0.14);
}

function synthSettle(): void {
  // The place card turning up — a soft low tone with a paper edge.
  synthTone({ freq: 294.66, duration: 0.4, peak: 0.12, type: 'sine', cutoff: 1400 });
  const ctx = audio.getAudioContext();
  if (!ctx) return;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 90, shaping: 1.8 });
  const master = audio.getMasterGain();
  if (!master) return;
  const t = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 1100;
  const { node: ng } = audio.envelope(ctx, { attack: 0.004, release: 0.1, peak: 0.07 });
  audio.connectThrough(src, lp, ng, master);
  src.start(t);
  src.stop(t + 0.12);
}

function synthStar(positionIndex = 0): void {
  // A small bright stamp — pitch steps up per star so a full run sings.
  const steps = [1.0, 1.12, 1.26, 1.33, 1.5];
  const f = 880 * (steps[positionIndex] ?? 1);
  synthTone({ freq: f, duration: 0.22, peak: 0.1, type: 'triangle', cutoff: 3200 });
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class AtlasSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: AtlasSoundName, positionIndex = 0): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'tick':
          synthTick();
          return;
        case 'pinDrop':
          synthPinDrop();
          return;
        case 'confirm':
          synthConfirm();
          return;
        case 'pageTurn':
          synthPageTurn();
          return;
        case 'arriveNear':
          synthArriveNear();
          return;
        case 'arriveFar':
          synthArriveFar();
          return;
        case 'settle':
          synthSettle();
          return;
        case 'star':
          synthStar(positionIndex);
          return;
      }
    });
  }
}

export const sound = new AtlasSound();
